import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import './CSS/PlatformFinance.css';
import './CSS/PlatformSupportTickets.css';

const EMPTY_FORM = {
  status: 'open',
  priority: 'medium',
  assigned_to_user_id: '',
  visibility: 'public',
  message: '',
};

export default function PlatformSupportTickets() {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [tickets, setTickets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    loadTickets();
  }, [filters.status]);

  useEffect(() => {
    if (!selectedTicketId) {
      setDetail(null);
      return;
    }
    loadDetail(selectedTicketId);
  }, [selectedTicketId]);

  const summary = useMemo(() => {
    const base = { open: 0, in_progress: 0, resolved: 0, closed: 0 };
    tickets.forEach((ticket) => {
      const key = String(ticket.status || '').toLowerCase();
      if (base[key] != null) base[key] += 1;
    });
    return base;
  }, [tickets]);

  const loadAgents = async () => {
    try {
      const res = await api.get('/api/support-tickets/platform/agents', { headers });
      setAgents(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      // keep page usable even if agents fail
    }
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.search.trim()) params.search = filters.search.trim();
      const res = await api.get('/api/support-tickets/platform/tickets', { headers, params });
      const rows = Array.isArray(res.data) ? res.data : [];
      setTickets(rows);
      if (!selectedTicketId && rows.length) {
        setSelectedTicketId(rows[0].id);
      } else if (selectedTicketId && !rows.some((row) => String(row.id) === String(selectedTicketId))) {
        setSelectedTicketId(rows[0]?.id || null);
      }
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to load support cases');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (ticketId) => {
    try {
      const res = await api.get(`/api/support-tickets/platform/tickets/${ticketId}`, { headers });
      const nextDetail = res.data || null;
      setDetail(nextDetail);
      setForm({
        status: nextDetail?.ticket?.status || 'open',
        priority: nextDetail?.ticket?.priority || 'medium',
        assigned_to_user_id: nextDetail?.ticket?.assigned_to_user_id || '',
        visibility: 'public',
        message: '',
      });
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to load support case detail');
    }
  };

  const applyFilters = () => {
    loadTickets();
  };

  const submitUpdate = async (e) => {
    e.preventDefault();
    if (!selectedTicketId) return;

    setSaving(true);
    try {
      await api.put(
        `/api/support-tickets/platform/tickets/${selectedTicketId}`,
        {
          status: form.status,
          priority: form.priority,
          assigned_to_user_id: form.assigned_to_user_id || null,
          visibility: form.visibility,
          message: form.message.trim() || null,
        },
        { headers }
      );

      await loadTickets();
      await loadDetail(selectedTicketId);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to update support case');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="platform-finance-page">
      <section className="platform-finance-hero">
        <div>
          <span className="platform-finance-kicker">Support Desk</span>
          <h1>Support ticket tracking</h1>
          <p>
            Track customer issues, assign cases, and reply from one shared support queue. Tickets
            created in the customer portal or PAMPA arrive here automatically.
          </p>
        </div>
      </section>

      <section className="platform-finance-summary-grid">
        <article className="platform-finance-stat">
          <div>
            <span>Open</span>
            <strong>{summary.open}</strong>
            <small>Needs first action</small>
          </div>
        </article>
        <article className="platform-finance-stat">
          <div>
            <span>In progress</span>
            <strong>{summary.in_progress}</strong>
            <small>Work in motion</small>
          </div>
        </article>
        <article className="platform-finance-stat">
          <div>
            <span>Resolved</span>
            <strong>{summary.resolved}</strong>
            <small>Ready to close</small>
          </div>
        </article>
        <article className="platform-finance-stat">
          <div>
            <span>Closed</span>
            <strong>{summary.closed}</strong>
            <small>Completed cases</small>
          </div>
        </article>
      </section>

      <section className="platform-finance-grid billing">
        <div className="platform-finance-surface">
          <div className="platform-finance-section-head">
            <div>
              <span className="platform-finance-chip">Queue</span>
              <h3>All support cases</h3>
            </div>
          </div>

          <div className="platform-finance-form grid-3">
            <label>
              <span>Status</span>
              <select value={filters.status} onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}>
                <option value="">All</option>
                <option value="open">Open</option>
                <option value="in_progress">In progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </label>
            <label>
              <span>Search</span>
              <input
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                placeholder="Ticket no, title, requester..."
              />
            </label>
            <button type="button" className="platform-finance-btn secondary" onClick={applyFilters}>
              Refresh queue
            </button>
          </div>

          <div className="platform-billing-list platform-support-list">
            {loading ? (
              <div className="platform-finance-note">Loading support cases...</div>
            ) : tickets.length ? (
              tickets.map((ticket) => (
                <button
                  key={ticket.id}
                  type="button"
                  className={`platform-billing-card ${String(selectedTicketId) === String(ticket.id) ? 'active' : ''}`}
                  onClick={() => setSelectedTicketId(ticket.id)}
                >
                  <div>
                    <strong>{ticket.ticket_number}</strong>
                    <span>{ticket.title}</span>
                    <small>
                      {ticket.tenant_name || '-'} • {ticket.created_by_name || '-'}
                    </small>
                  </div>
                  <div className="platform-billing-card-side">
                    <em>{ticket.status}</em>
                    <span>{ticket.priority}</span>
                  </div>
                </button>
              ))
            ) : (
              <div className="platform-finance-note">No support cases matched the current filters.</div>
            )}
          </div>
        </div>

        <div className="platform-finance-surface">
          <div className="platform-finance-section-head">
            <div>
              <span className="platform-finance-chip">Detail</span>
              <h3>{detail?.ticket?.title || 'Select a case'}</h3>
            </div>
          </div>

          {detail?.ticket ? (
            <>
              <div className="platform-support-detail-head">
                <div className="platform-finance-chip-list">
                  <span className="platform-finance-chip-pill">{detail.ticket.ticket_number}</span>
                  <span className="platform-finance-chip-pill">{detail.ticket.status}</span>
                  <span className="platform-finance-chip-pill">{detail.ticket.priority}</span>
                  <span className="platform-finance-chip-pill">{detail.ticket.tenant_name || '-'}</span>
                </div>
                <p className="platform-finance-note">{detail.ticket.description}</p>
              </div>

              <form className="platform-finance-form" onSubmit={submitUpdate}>
                <div className="platform-finance-form grid-3">
                  <label>
                    <span>Status</span>
                    <select value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}>
                      <option value="open">Open</option>
                      <option value="in_progress">In progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </label>
                  <label>
                    <span>Priority</span>
                    <select value={form.priority} onChange={(e) => setForm((prev) => ({ ...prev, priority: e.target.value }))}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </label>
                  <label>
                    <span>Assign to</span>
                    <select
                      value={form.assigned_to_user_id}
                      onChange={(e) => setForm((prev) => ({ ...prev, assigned_to_user_id: e.target.value }))}
                    >
                      <option value="">Unassigned</option>
                      {agents.map((agent) => (
                        <option key={agent.id} value={agent.id}>
                          {agent.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="platform-finance-form grid-2">
                  <label>
                    <span>Reply visibility</span>
                    <select value={form.visibility} onChange={(e) => setForm((prev) => ({ ...prev, visibility: e.target.value }))}>
                      <option value="public">Public reply</option>
                      <option value="internal">Internal note</option>
                    </select>
                  </label>
                </div>

                <label>
                  <span>Reply or note</span>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                    placeholder="Write the next update for this case."
                  />
                </label>

                <button type="submit" className="platform-finance-btn primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save update'}
                </button>
              </form>

              <div className="platform-support-timeline">
                {(detail.updates || []).map((update) => (
                  <article key={update.id} className="platform-support-update">
                    <div className="platform-support-update-head">
                      <strong>{update.author_name || 'FuelFlow'}</strong>
                      <span>{update.author_role || '-'}</span>
                      <small>{new Date(update.created_at).toLocaleString('en-IN')}</small>
                    </div>
                    <p>{update.message}</p>
                  </article>
                ))}
              </div>
            </>
          ) : (
            <div className="platform-finance-note">Pick a support case from the left to manage it.</div>
          )}
        </div>
      </section>
    </div>
  );
}
